import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/useAuth';
import { checkDatabaseConnectivity } from '@/utils/checkDatabaseConnectivity';
import { GmailService } from '@/services/gmailService';
import { TechnologyStatus } from '@/types/technology-status';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('TechnologyStatus', 'system-status');
const GMAIL_STATUS_REFRESH = 'gmail-status-refresh';

type ConnectionStatus = {
  connected: boolean;
  message: string;
};

function waitForRetry(delayMs: number, signal: AbortSignal): Promise<boolean> {
  if (signal.aborted) {
    return Promise.resolve(false);
  }

  return new Promise((resolve) => {
    const handleAbort = () => {
      clearTimeout(timer);
      resolve(false);
    };
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', handleAbort);
      resolve(true);
    }, delayMs);

    signal.addEventListener('abort', handleAbort, { once: true });
  });
}

async function checkDatabase(signal: AbortSignal): Promise<ConnectionStatus> {
  try {
    const dbStatus = await checkDatabaseConnectivity(signal);
    return {
      connected: dbStatus.connected,
      message: dbStatus.connected ? 'Database ready' : 'Database not connected'
    };
  } catch {
    return { connected: false, message: 'Database error' };
  }
}

async function checkGmail(
  signal: AbortSignal,
  retryCount: number = 0
): Promise<ConnectionStatus> {
  if (signal.aborted) {
    return { connected: false, message: 'Gmail check cancelled' };
  }

  try {
    const isConnected = await GmailService.checkGmailConnection();
    return {
      connected: isConnected,
      message: isConnected ? 'Gmail connected' : 'Gmail not connected'
    };
  } catch (error) {
    if (signal.aborted) {
      return { connected: false, message: 'Gmail check cancelled' };
    }

    log.error('Error checking Gmail status', undefined, error as Error);
    if (retryCount < 1) {
      log.info('[useTechnologyStatus] Retrying Gmail check...');
      const shouldRetry = await waitForRetry(1000, signal);
      if (shouldRetry) {
        return checkGmail(signal, retryCount + 1);
      }
    }

    return { connected: false, message: 'Gmail check failed' };
  }
}

function checkRealtime(): ConnectionStatus {
  return {
    connected: true,
    message: 'Real-time sync active'
  };
}

export const useTechnologyStatus = (includeRealtime: boolean = false) => {
  const { session, user } = useAuth();
  const [status, setStatus] = useState<TechnologyStatus>({
    database: { connected: false },
    gmail: { connected: false },
    realtime: { connected: false },
    overall: { operational: false, message: 'Checking systems...' }
  });
  const lifecycleAbortRef = useRef<AbortController | null>(null);
  const activeCheckSignals = useRef<WeakSet<AbortSignal>>(new WeakSet());
  const hasAuthenticatedSession = Boolean(
    user
    && session?.access_token
    && session.user?.id === user.id
  );

  const updateOverallStatus = useCallback((
    db: ConnectionStatus,
    gmail: ConnectionStatus,
    realtime: ConnectionStatus
  ) => {
    const allConnected = db.connected
      && gmail.connected
      && (!includeRealtime || realtime.connected);

    if (allConnected) {
      return {
        operational: true,
        message: 'All Technology Operational'
      };
    }

    const issues: string[] = [];
    if (!db.connected) {
      issues.push('Database');
    }
    if (!gmail.connected) {
      issues.push('Gmail');
    }
    if (includeRealtime && !realtime.connected) {
      issues.push('Real-time sync');
    }

    return {
      operational: false,
      message: `${issues.join(', ')} not connected`
    };
  }, [includeRealtime]);

  const performChecks = useCallback(async (signal: AbortSignal) => {
    if (
      signal.aborted
      || !hasAuthenticatedSession
      || activeCheckSignals.current.has(signal)
    ) {
      return;
    }

    activeCheckSignals.current.add(signal);
    try {
      const [database, gmail] = await Promise.all([
        checkDatabase(signal),
        checkGmail(signal)
      ]);
      if (signal.aborted) {
        return;
      }

      const realtime = includeRealtime
        ? checkRealtime()
        : { connected: true, message: 'Real-time sync not requested' };
      const overall = updateOverallStatus(database, gmail, realtime);

      setStatus((previousStatus) => {
        if (previousStatus.gmail.connected && !gmail.connected) {
          log.warn('Gmail connection lost, will attempt recovery on next check');
        }
        if (!previousStatus.gmail.connected && gmail.connected) {
          log.info('Gmail connection recovered');
        }

        return {
          database,
          gmail,
          realtime,
          overall
        };
      });
    } finally {
      activeCheckSignals.current.delete(signal);
    }
  }, [hasAuthenticatedSession, includeRealtime, updateOverallStatus]);

  const refreshStatus = useCallback(async () => {
    const controller = lifecycleAbortRef.current;
    if (!controller || controller.signal.aborted || !hasAuthenticatedSession) {
      return;
    }

    log.debug('Manual status refresh triggered');
    GmailService.clearCache();
    await performChecks(controller.signal);
  }, [hasAuthenticatedSession, performChecks]);

  useEffect(() => {
    const controller = new AbortController();
    lifecycleAbortRef.current = controller;

    const clearLifecycle = () => {
      controller.abort();
      activeCheckSignals.current.delete(controller.signal);
      if (lifecycleAbortRef.current === controller) {
        lifecycleAbortRef.current = null;
      }
    };

    if (!hasAuthenticatedSession) {
      return clearLifecycle;
    }

    void performChecks(controller.signal);

    const interval = setInterval(() => {
      void performChecks(controller.signal);
    }, 30000);

    const gmailInterval = setInterval(() => {
      if (window.location.pathname !== '/internal' || controller.signal.aborted) {
        return;
      }

      void GmailService.checkGmailConnection(true).then((gmailConnected) => {
        if (controller.signal.aborted) {
          return;
        }
        setStatus((previousStatus) => ({
          ...previousStatus,
          gmail: {
            connected: gmailConnected,
            message: gmailConnected ? 'Gmail connected' : 'Gmail not connected'
          }
        }));
      }).catch((error) => {
        if (!controller.signal.aborted) {
          log.error(
            '[useTechnologyStatus] Gmail periodic check failed',
            undefined,
            error as Error
          );
        }
      });
    }, 5 * 60 * 1000);

    const handleGmailRefresh = () => {
      log.debug('Gmail refresh event received');
      void performChecks(controller.signal);
    };

    window.addEventListener(GMAIL_STATUS_REFRESH, handleGmailRefresh);

    return () => {
      clearInterval(interval);
      clearInterval(gmailInterval);
      window.removeEventListener(GMAIL_STATUS_REFRESH, handleGmailRefresh);
      clearLifecycle();
    };
  }, [hasAuthenticatedSession, performChecks]);

  return { status, refreshStatus };
};
